import React, { useState } from 'react';
import './PartnerInquiryPage.css';

const PartnerInquiryPage = () => {
  const [formData, setFormData] = useState({
    schoolName: '',
    schoolType: '',
    city: '',
    state: '',
    website: '',
    contactPersonName: '',
    contactPersonDesignation: '',
    contactEmail: '',
    contactPhone: '',
    studentCount: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.schoolName.trim()) {
      newErrors.schoolName = 'Institution name is required';
    }
    if (!formData.schoolType) {
      newErrors.schoolType = 'Please select institution type';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'Please select state';
    }
    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email';
    }
    if (!formData.contactPhone.trim()) {
      newErrors.contactPhone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.contactPhone)) {
      newErrors.contactPhone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await fetch('https://goocampus.ai/api/institution-inquiry/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Thank you! Our team will contact you within 24-48 hours.'
        });
        // Reset form
        setFormData({
          schoolName: '',
          schoolType: '',
          city: '',
          state: '',
          website: '',
          contactPersonName: '',
          contactPersonDesignation: '',
          contactEmail: '',
          contactPhone: '',
          studentCount: '',
          message: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Failed to submit. Please try again.'
        });
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="partner-inquiry-page">

      <section className="partner-hero">
        <div className="container">
          <h1>Partner With 12thPlus</h1>
          <p>Empower your students with comprehensive career guidance under your brand</p>
        </div>
      </section>

      <section className="partner-form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-info">
              <h2>Why Partner With Us?</h2>
              <ul className="benefits-list">
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>White-labelled platform with your branding</span>
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>No operational burden on your staff</span>
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Enhance student satisfaction and outcomes</span>
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Quick setup with ongoing support</span>
                </li>
                <li>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Comprehensive career guidance tools</span>
                </li>
              </ul>
            </div>

            <form className="inquiry-form" onSubmit={handleSubmit}>
              <h3>Get In Touch</h3>
              <p className="form-subtitle">Fill out the form and our team will contact you within 24-48 hours.</p>

              {submitStatus && (
                <div className={`status-message ${submitStatus.type}`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="form-section-title">Institution Details</div>

              <div className="form-group">
                <label htmlFor="schoolName">Institution Name *</label>
                <input
                  type="text"
                  id="schoolName"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleChange}
                  placeholder="Enter your school/college name"
                  className={errors.schoolName ? 'error' : ''}
                />
                {errors.schoolName && <span className="error-text">{errors.schoolName}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="schoolType">Institution Type *</label>
                  <select
                    id="schoolType"
                    name="schoolType"
                    value={formData.schoolType}
                    onChange={handleChange}
                    className={errors.schoolType ? 'error' : ''}
                  >
                    <option value="">Select type</option>
                    <option value="school">School (Class 11-12)</option>
                    <option value="college">Junior College</option>
                    <option value="coaching">Coaching Center</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.schoolType && <span className="error-text">{errors.schoolType}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="studentCount">Approximate Student Count</label>
                  <select
                    id="studentCount"
                    name="studentCount"
                    value={formData.studentCount}
                    onChange={handleChange}
                  >
                    <option value="">Select range</option>
                    <option value="less-than-500">Less than 500</option>
                    <option value="500-1000">500 - 1,000</option>
                    <option value="1000-2000">1,000 - 2,000</option>
                    <option value="2000-5000">2,000 - 5,000</option>
                    <option value="more-than-5000">More than 5,000</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className={errors.city ? 'error' : ''}
                  />
                  {errors.city && <span className="error-text">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className={errors.state ? 'error' : ''}
                  >
                    <option value="">Select state</option>
                    {indianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  {errors.state && <span className="error-text">{errors.state}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="website">Website (Optional)</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.yourschool.com"
                />
              </div>

              <div className="form-section-title">Contact Person Details</div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactPersonName">Full Name *</label>
                  <input
                    type="text"
                    id="contactPersonName"
                    name="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={handleChange}
                    placeholder="Contact person name"
                    className={errors.contactPersonName ? 'error' : ''}
                  />
                  {errors.contactPersonName && <span className="error-text">{errors.contactPersonName}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="contactPersonDesignation">Designation</label>
                  <input
                    type="text"
                    id="contactPersonDesignation"
                    name="contactPersonDesignation"
                    value={formData.contactPersonDesignation}
                    onChange={handleChange}
                    placeholder="e.g., Principal, Career Counsellor"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactEmail">Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    placeholder="email@school.com"
                    className={errors.contactEmail ? 'error' : ''}
                  />
                  {errors.contactEmail && <span className="error-text">{errors.contactEmail}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="contactPhone">Phone *</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleChange}
                    placeholder="10-digit phone number"
                    className={errors.contactPhone ? 'error' : ''}
                  />
                  {errors.contactPhone && <span className="error-text">{errors.contactPhone}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Additional Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your requirements or any questions you have..."
                  rows="4"
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerInquiryPage;
